import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { MapPolygon, Nestable } from '@core/model/summary';
import { Datacenter } from '@core/model/datacenter';
import { getDatacentersByProviderSuccess, SourceEnum } from '@core/ngrx/actions/datacenter.actions';
import { filterPointsInArea } from '@core/util/geo-utils';
import * as mapboxgl from 'mapbox-gl';
import {DataCentersService} from '@core/service/data-centers.service';
import {MapService} from '@core/service/map.service';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import {DistanceUnits, UserMap} from '@core/model/user-map';
import * as fromConsts from '@core/consts/map.constants';
import {
  DATACENTERS_BATCH_SIZE, MARKERS_SOURCE_ID,
  MARKET_CIRCLE_FILL_LAYER_ID,
  MARKET_CIRCLE_LAYER_ID,
  MARKET_LABEL_LAYER_ID,
  SOURCE_ID,
  SQUARE_INFO_TEXT_LAYER_ID
} from '@core/consts/map.constants';
import {Store} from '@ngrx/store';
import {filter, firstValueFrom, Observable, skip, Subject, switchMap, take, takeUntil, tap} from 'rxjs';
import {MapConfigSelector} from '@core/ngrx/selectors/map-config.selector';
import {DynamicComponentService} from '@core/service/dynamic-component.service';
import {PolygonPopupComponent} from '../polygon-popup/polygon-popup.component';
import {Actions, ofType} from '@ngrx/effects';
import {
  geoActions,
  mapConfigActions,
  mapFilesActions,
  polygonActions,
  providersActions
} from '@core/ngrx/actions';
import {DialogService, DynamicDialogComponent, DynamicDialogRef} from 'primeng/dynamicdialog';
import {NewDataCenterPopupComponent} from '../new-data-center-popup/new-data-center-popup.component';
import {GeoInfo} from '@core/model/geo-info';
import * as _ from 'lodash';
import * as MapboxDrawGeodesic from 'mapbox-gl-draw-geodesic';
import {CustomDrawControl} from '@core/util/custom-draw-control';
import {FileDownloaderService} from '@core/service/file-downloader.service';
import {ResizeEvent} from 'angular-resizable-element';
import {GeoSelector} from "@core/ngrx/selectors/geo.selector";
import {MapLayer} from '@core/model/map-layer.interface';
import {MapLayerPopupComponent} from '../map-layer-popup/map-layer-popup.component';
import {AvailabilityZonePopupComponent} from '../availability-zone-popup/availability-zone-popup.component';
import {MapFilesSelector} from "@core/ngrx/selectors/map-files.selector";
import {MapFile} from "@core/model/map-file";
import {AssetType} from "@core/model/user-map-file";
import {MapboxUser} from "@core/model/mapbox-user";
import {UsersSelector} from "@core/ngrx/selectors/users.selector";
import {DatacenterV2} from "@core/model/datacenter-v2";
import * as datacenterActions from "@core/ngrx/actions/datacenter.actions";
import {PolygonsHandler} from "@core/polygons.handler";
import {PolygonsSelector} from "@core/ngrx/selectors/polygons.selector";
import {DatacentersHandler} from "@core/datacenters.handler";
import * as ProviderActions from "@core/ngrx/actions/provider.actions";
import {LabelsHandler} from '@core/labels-handler';

@Component({
  selector: 'app-map-display',
  templateUrl: './map-display.component.html',
  styleUrls: ['./map-display.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class MapDisplayComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
  labelsHandler: LabelsHandler = inject(LabelsHandler);
  users: MapboxUser[] = [];
  mapfileUser: MapboxUser;
  style: string;
  isMiles: boolean = true;
  arePolygonsLoaded: boolean = false;
  labelModalVisible: boolean = false;
  layerId = 'light';
  userMap: Observable<UserMap> = this.mapConfigSelector.getUserMap();
  fileMetadata: MapFile;
  public map: mapboxgl.Map;

  displayLines: boolean = true;
  displayMarkers: boolean = true;
  displayInfo: boolean = true;
  displayPolygons: boolean = true;
  paintAreas: boolean = false;
  displayControls: boolean = true;
  legendCollapsed = true;
  displayTilesetMetadata = false;
  tilesetId: string;

  navigationControl = new mapboxgl.NavigationControl();
  fullscreenControl = new mapboxgl.FullscreenControl();
  polygonControl = new MapboxDraw({
    displayControlsDefault: false,
    // Select which mapbox-gl-draw control buttons to add to the map.
    controls: {
      polygon: true,
      line_string: true,
      trash: true,
      draw_circle: true,
    },
    modes: {
      ...MapboxDrawGeodesic.enable(MapboxDraw.modes),
    },
    styles: fromConsts.MAP_DRAW_STYLE,
    userProperties: true,
  });


  draw = new CustomDrawControl({
    draw: this.polygonControl,
    buttons: [
      {
        events: [
          {
            on: 'click',
            action: () => {
              if (
                (event.target as any).parentElement.classList.contains('active')
              ) {
                (event.target as any).parentElement.classList.remove('active');
                this.polygonControl.changeMode('simple_select');
              } else {
                (event.target as any).parentElement.classList.add('active');
                this.polygonControl.changeMode('draw_circle');
              }
            },
          },
        ],
        content: '<span class="circle"></span>',
        classes: [],
      },
    ],
  });

  polygonPopup: mapboxgl.Popup;

  destroy$: Subject<boolean> = new Subject<boolean>();

  mwThreshold: number = 0;
  areMarkersBlocked: boolean = true;
  isReadOnly: boolean = false;
  datacenters: Datacenter[] = [];
  private polygonsMap: Map<string, string> = new Map<string, string>();

  @ViewChild('resizableContainer') resizableContainer: ElementRef<HTMLElement>;
  private initialWidth: number;
  isResizing = false;
  private resizeStartX: number;
  searchGeoInfoResult: Observable<GeoInfo> = this.geoSelector.getLocation();

  @ViewChild('fullscreenContainer') fullscreenContainer: ElementRef;

  @ViewChild('dialogContainer', {read: ViewContainerRef}) dialogContainer: ViewContainerRef;

  isFullscreen: boolean = false;

  public lastKnownWidth: string;
  private mapCreationDate: Date;

  private layerPopup: mapboxgl.Popup = null;
  fiberMapFiles: Observable<MapFile[]> = this.mapFilesSelector.getFiberFiles();
  powerMapFiles: Observable<MapFile[]> = this.mapFilesSelector.getPowerFiles();
  landMapFiles: Observable<MapFile[]> = this.mapFilesSelector.getLandFiles();
  railroadMapFiles: Observable<MapFile[]> = this.mapFilesSelector.getRailroadFiles();
  oilGasMapFiles: Observable<MapFile[]> = this.mapFilesSelector.getOilGasFiles();


  private currentPopup: mapboxgl.Popup | null = null;

  constructor(
    private dataCentersService: DataCentersService,
    private dynamicComponentService: DynamicComponentService,
    private mapService: MapService,
    private readonly store: Store,
    private mapConfigSelector: MapConfigSelector,
    private appRef: ApplicationRef,
    private actions$: Actions,
    public dialogService: DialogService,
    private fileDownloader: FileDownloaderService,
    private ngZone: NgZone,
    private renderer: Renderer2,
    private geoSelector: GeoSelector,
    private cdr: ChangeDetectorRef,
    private mapFilesSelector: MapFilesSelector,
    private usersSelector: UsersSelector,
    private polygonsHandler: PolygonsHandler,
    private polygonsSelector: PolygonsSelector,
    private datacentersHandler: DatacentersHandler,
    private datacentersService: DataCentersService,
  ) {
    super();
  }

  ngOnInit(): void {
    //create map bounds and center
    this.changeStyle(this.layerId, true);
    this.mapConfigSelector
      .getMwThreshold()
      .pipe(
        takeUntil(this.destroy$),
        tap((threshold: number) => this.onMwThresholdChange(threshold))
      )
      .subscribe();

    this.mapConfigSelector
      .isReadOnly()
      .pipe(
        takeUntil(this.destroy$),
        tap((isReadOnly: boolean) => (this.isReadOnly = isReadOnly))
      )
      .subscribe();

    this.mapFilesSelector
      .getFileMetadata()
      .pipe(
        takeUntil(this.destroy$),
        filter((metadata: MapFile) => !!metadata),
        tap((file) => {
          this.displayTilesetMetadata = true;
          this.fileMetadata = file;
          this.mapfileUser = this.users.find(user => user.userId == file.userId);
        })
      )
      .subscribe();

    this.mapConfigSelector
      .getDistanceUnits()
      .pipe(
        takeUntil(this.destroy$),
        tap((units: DistanceUnits) => {
          this.isMiles = units == DistanceUnits.MILES;
          if (this.map) {
            this.polygonsHandler.updateLineLabelsForNewUnits(this.map, units);
          }
        })
      )
      .subscribe();

    document.addEventListener('fullscreenchange', this.handleFullscreenChange);

    this.mapConfigSelector
      .getInfoLayerVisibility()
      .pipe(
        takeUntil(this.destroy$),
        tap((visible: boolean) => {
          this.displayInfo = visible;
          if (this.map?.getLayer(SQUARE_INFO_TEXT_LAYER_ID)) {
            this.map.setLayoutProperty(
              SQUARE_INFO_TEXT_LAYER_ID,
              'visibility',
              visible ? 'visible' : 'none'
            );
          }
        })
      )
      .subscribe();

    this.actions$.pipe(
      ofType(mapConfigActions.CHANGED_PROVIDER_IMAGE),
      takeUntil(this.destroy$),
      tap(() => {
        this.map.easeTo({
          zoom: this.map.getZoom(),
          duration: 50,
          essential: true,
          easing: (t) => t,
        });
      })
    ).subscribe();
  }

  ngAfterViewInit() {
    if (!this.legendCollapsed) {
      this.initialWidth = this.resizableContainer.nativeElement.offsetWidth;
    }
    this.dialogService.dialogComponentRefMap.forEach((value, key) => {
      if (value.instance instanceof DynamicDialogComponent ||
        value.componentType?.name === 'DynamicDialogComponent') {
        const dialogInstance = value.instance as any;
        if (typeof dialogInstance.appendTo === 'undefined') {
          dialogInstance.appendTo = this.dialogContainer.element.nativeElement;
        }
      }
    });
  }

  onUserMapLoaded(userMap: UserMap) {
    this.store.dispatch(
      providersActions.loadProviders({mapId: userMap._id})
    );
    this.store.dispatch(
      mapFilesActions.loadFiles()
    );
    this.mapCreationDate = userMap?.creationDate;
    this.mapFilesSelector
      .getMapFiles()
      .pipe(
        filter((mapFiles: MapFile[]) => !!mapFiles?.length),
        takeUntil(this.destroy$),
        tap((mapFiles: MapFile[]) => this.onMapFilesSelectionChanged(mapFiles))
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.store.dispatch(mapFilesActions.nukeStore());
    this.store.dispatch(
      datacenterActions.nukeStore()
    );
    this.store.dispatch(
      providersActions.nukeStore()
    );
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    if (this.isFullscreen) {
      this.exitFullscreen();
    }
  }

  onMwThresholdChange(mwThreshold: number) {
    this.mwThreshold = mwThreshold;
  }

  onMwThresholdValueChange(event) {
    this.store.dispatch(
      mapConfigActions.mwThresholdChange({
        value: event.value,
      })
    );
  }

  onloadmap(map) {
    this.map = map.target;
    this.mapService.map = this.map;

    this.map.addControl(this.navigationControl);
    this.map.addControl(new CustomFullscreenControl(this.toggleFullscreen.bind(this)));
    this.map.addControl(this.draw);

    this.mapConfigSelector
      .getUserMap()
      .pipe(
        filter((userMap: UserMap) => !!userMap),
        take(1),
        tap((userMap: UserMap) => {
          this.polygonsHandler.addPolygonsSourceData(userMap, this.map, this.polygonControl);
          this.labelsHandler.addLabelSourceData(this.map);
          for (let label of userMap.mapLabels || []) {
            this.labelsHandler.addLabelToMap(this.map, label.text, label.latitude, label.longitude);
          }
          this.onUserMapLoaded(userMap)
        })
      )
      .subscribe();

    this.actions$.pipe(
      ofType(mapConfigActions.loadUserMapSuccess),
      takeUntil(this.destroy$),
      tap((action) => {
        this.labelsHandler.addLabelSourceData(this.map);

      })
    ).subscribe();

    let _this = this;

    this.actions$
      .pipe(
        ofType(polygonActions.UPDATE_POLYGON_SUM),
        takeUntil(this.destroy$),
        tap((_) => {
          this.polygonsHandler.modifyPolygonSumOnDatacenterChange(this.map, this.polygonControl);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(mapConfigActions.MW_THRESHOLD_CHANGE),
        takeUntil(this.destroy$),
        tap((action: any) => {
          this.datacentersHandler.onThresholdChanged(this.map, action.value)
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(providersActions.PROVIDERS_LOADED),
        take(1),
        tap((_) => {
          this.mapConfigSelector
            .getUserMap()
            .pipe(
              filter((userMap: UserMap) => !!userMap),
              take(1),
              tap((userMap: UserMap) => {
                this.store.dispatch(
                  datacenterActions.getDatacenters({mapId: userMap._id, pageSize: DATACENTERS_BATCH_SIZE, lastId: null})
                );
              })
            )
            .subscribe();

        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(polygonActions.POLYGON_MODIFIED),
        takeUntil(this.destroy$),
        tap((data: any) => {
          this.polygonsHandler.onPolygonModified(this.map, data.polygon, this.polygonControl);
          this.polygonPopup.remove();
          this.appRef.tick();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(mapConfigActions.POPUP_CANCELLED),
        takeUntil(this.destroy$),
        tap((data) => {
          this.popupSaved(data);
          this.polygonPopup.remove();
          this.appRef.tick();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(polygonActions.POLYGON_SELECTED),
        takeUntil(this.destroy$),
        tap((data: any) => this.addPopup(data.polygon))
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(geoActions.CENTER_MAP),
        takeUntil(this.destroy$),
        tap((data) => this.centerMap((data as any).info))
      )
      .subscribe();

    this.map.on('draw.selectionchange', (event) => {
      if (event.features.length) {
        this.mapConfigSelector
          .areMarkersBlocked()
          .pipe(
            take(1),
            tap((areMarkersBlocked: boolean) => {
              if (areMarkersBlocked) {
                this.polygonControl.changeMode('simple_select');
                const polygon = event.features[0];
                const provider = {
                  id: polygon.id,
                  name: polygon?.properties?.label || 'Unnamed polygon',
                } as any
                if (this.polygonsMap.has(polygon.id)) {
                  this.polygonsMap.delete(polygon.id);
                  this.store.dispatch(providersActions.providerUnselected({
                    provider: provider
                  }));
                } else {
                  this.polygonsMap.set(polygon.id, polygon.id);
                  this.mapConfigSelector.getUserMap().pipe(
                    take(1),
                    tap(map => {
                      const [, featureCollections] = this.datacentersService.getFeatureCollectionFromSource(this.map, MARKERS_SOURCE_ID);
                      const datacenters = featureCollections.features?.map(f => f.properties) ?? [];
                      const datacentersInside = filterPointsInArea(polygon, datacenters);
                      this.store.dispatch(
                        getDatacentersByProviderSuccess({
                          provider,
                          datacenters: datacentersInside,
                          sourceData: {
                            source: SourceEnum.POLYGON,
                            polygon: polygon,
                          },
                        })
                      );
                    })
                  ).subscribe();
                }
              }
            })
          )
          .subscribe();
      }
    });


    this.map.on('draw.create', (event) => {
      this.polygonsHandler.onDrawCreate(this.map, event, this.polygonControl);
    });

    this.map.on('draw.update', (event) => {
      this.polygonsHandler.onPolygonShapeModified(this.map, event, this.polygonControl);
    });

    this.map.on('draw.delete', (event) => {
      this.polygonsHandler.onPolygonShapeDeleted(this.map, event);
    });

    this.map.on('contextmenu', 'gl-draw-polygon-fill.cold', (event) => {
      if (!this.areMarkersBlocked) {
        this.polygonsHandler.onPolygonRightClick(event, this.polygonControl);
      }
    });

    this.map.on('contextmenu', 'marketCircleFillLayerId', (event) => {
      if (!this.paintAreas) return;
      this.mapService.onMarketRightClick(event, _this, this.polygonControl);
    });
    this.map.on('contextmenu', 'marketCircleLayerId', (event) => {
      if (!this.paintAreas) return;
      this.mapService.onMarketRightClick(event, _this, this.polygonControl);
    });

    this.map.on('contextmenu', (event) => {
      if (this.currentPopup) {
        this.currentPopup.remove();
      }

      const features = this.map.queryRenderedFeatures(event.point);
      const azFeature = features.find(f => (f.layer.id.startsWith('az')));

      if (azFeature) {
        const popupElement = document.querySelector('.polygon-popup') as HTMLElement;
        if (popupElement) {
          popupElement.style.display = 'none';
        }

        const azId = azFeature.properties['azId'];
        const provider = azFeature.properties['provider'];

        const popupContent = this.dynamicComponentService.injectComponent(
          AvailabilityZonePopupComponent,
          (component) => {
            component.provider = provider;
            component.availabilityZoneId = azId;
          }
        );

        this.currentPopup = new mapboxgl.Popup({
          closeButton: true,
          closeOnClick: true,
          className: 'az-popup',
          maxWidth: '300px',
          focusAfterOpen: true
        })
          .setLngLat(event.lngLat)
          .setDOMContent(popupContent)
          .addTo(this.map);
      }
      this.appRef.tick();
    });
    this.map.on('style.load', (event) => {
      this.mapConfigSelector.getUserMap().pipe(
        take(1),
        tap((userMap: UserMap) => {
          this.polygonsHandler.addPolygonsSourceData(userMap, this.map, this.polygonControl);
          this.store.dispatch(
            datacenterActions.getDatacenters({mapId: userMap._id, pageSize: DATACENTERS_BATCH_SIZE, lastId: null})
          );
          if (userMap.polygons) {
            this.polygonsSelector.getPolygonsBk().pipe(
              take(1),
              filter(polygonsBk => polygonsBk),
              tap(polygonsBk => {
                this.polygonControl.set(polygonsBk)
                this.store.dispatch(polygonActions.nukeBkPolygonData());
              })
            ).subscribe();
          }
        })
      ).subscribe();
    });

    this.map.on('click', (event) => {
      if (this.polygonControl.getMode() == 'draw_line_string') {
        this.polygonControl.getAll().features.forEach((f) => {
          if (f.geometry.type == 'LineString') {
            if (f.geometry.coordinates.length == 3) {
              this.polygonControl.changeMode('simple_select');
            }
          }
        });
      }
    });

    this.map.on('zoomend', () => {

    });

    this.map.on('moveend', () => {

    });

    this.map.on('idle', () => {
    });
    this.mapConfigSelector
      .areMarkersBlocked()
      .pipe(
        takeUntil(this.destroy$),
        tap((areBlocked: boolean) => {
          this.areMarkersBlocked = areBlocked;
          if (!this.arePolygonsLoaded) {
            this.mapConfigSelector.getUserMap().pipe(
              filter(userMap => !!userMap),
              take(1),
              tap((map) => {
                if (map.polygons) {
                  const fc = JSON.parse(map.polygons);
                  this.polygonControl.set(fc)
                  this.arePolygonsLoaded = true;
                }
              })
            ).subscribe();
          }
          if (this.areMarkersBlocked) {
            (
              document.getElementsByClassName('mapboxgl-ctrl-group')[2] as any
            ).style.display = 'none';
          } else {
            (
              document.getElementsByClassName('mapboxgl-ctrl-group')[2] as any
            ).style.display = '';
          }
        })
      )
      .subscribe();
    this.legendCollapsed = false;
  }

  addPopup(selectedPolygon: MapPolygon) {
    if (this.polygonPopup) {
      this.polygonPopup.remove();
    }

    const polygonModel: MapPolygon = {
      ...selectedPolygon
    };

    const popupContent = this.createPopupContent(polygonModel);

    this.polygonPopup = new mapboxgl.Popup({closeButton: false, className: 'polygon-popup'})
      .setLngLat(polygonModel.center as any)
      .setDOMContent(popupContent)
      .addTo(this.map);

    this.appRef.tick();
  }

  private createPopupContent(polygonModel: MapPolygon): HTMLElement {
    return this.dynamicComponentService.injectComponent(
      PolygonPopupComponent,
      (component) => component.polygon = polygonModel
    );
  }


  popupSaved(data) {
    if (data.polygonData) {
      this.polygonControl.setFeatureProperty(
        data.polygonData.auxId,
        'color',
        data.polygonData.color
      );

      this.polygonControl.setFeatureProperty(
        data.polygonData.auxId,
        'opacity',
        data.polygonData.opacity
      );

      this.polygonControl.setFeatureProperty(
        data.polygonData.auxId,
        'user_opacity',
        data.polygonData.opacity
      );

      this.polygonControl.add(this.polygonControl.get(data.polygonData.auxId));
    }
    this.polygonPopup.remove();
    this.appRef.tick();
  }

  toggleAreaCircles() {
    this.paintAreas = !this.paintAreas;
    this.updateLayerVisibility();
  }

  toggleLines(isDisplaying) {
    this.displayLines = isDisplaying;
    this.polygonsHandler.toggleLinesVisibility(this.map, isDisplaying);
  }

  togglePolygons(isDisplaying) {
    this.displayPolygons = isDisplaying;
    this.polygonsHandler.togglePolygonsVisibility(this.map, isDisplaying);
  }

  toggleInfoSquare() {
    this.displayInfo = !this.displayInfo;
    this.polygonsHandler.toggleInfoVisibility(this.map, this.displayInfo);
  }

  toggleMapControls() {
    this.displayControls = !this.displayControls;
    if (this.displayControls) {
      this.map.addControl(this.navigationControl);
      this.map.addControl(this.fullscreenControl);
      this.map.addControl(this.polygonControl);
    } else {
      this.map.removeControl(this.navigationControl);
      this.map.removeControl(this.fullscreenControl);
      this.map.removeControl(this.polygonControl);
    }
  }

  onMapFilesSelectionChanged(mapFiles: MapFile[]) {
    mapFiles.forEach((mapFile: MapFile) => {
      const sourceId = `${mapFile.tilesetId}_SEP_${mapFile._id}_SEP_${mapFile.userId}`;
      const layerIdPoint = `${mapFile._id}_LAYER_ID_Point`;
      const layerIdLine = `${mapFile._id}_LAYER_ID_Line`;
      const layerIdFill = `${mapFile._id}_LAYER_ID_Fill`;
      const layerIdFillLine = `${mapFile._id}_LAYER_ID_Fill_Line`;
      const existingSource = this.map.getSource(sourceId);
      const existingPointLayer = this.map.getLayer(layerIdPoint);
      const existingLineLayer = this.map.getLayer(layerIdLine);
      const existingFillLayer = this.map.getLayer(layerIdFill);
      const existingFillLine = this.map.getLayer(layerIdFillLine);
      if (existingPointLayer) {
        this.map.off('click', layerIdPoint, this.onClickTileset);
        this.map.removeLayer(layerIdPoint);
      }
      if (existingFillLine) {
        this.map.off('click', layerIdFillLine, this.onClickTileset);
        this.map.removeLayer(layerIdFillLine);
      }
      if (existingFillLayer) {
        this.map.off('click', layerIdFill, this.onClickTileset);
        this.map.removeLayer(layerIdFill);
      }
      if (existingLineLayer) {
        this.map.off('click', layerIdLine, this.onClickTileset);
        this.map.removeLayer(layerIdLine);
      }
      if (existingSource) {
        this.map.removeSource(sourceId);
      }
      if (mapFile.fileAssets?.some(asset => asset.selected) && mapFile.tilesetId && sourceId) {
        this.map.addSource(sourceId, {
          "url": `mapbox://${mapFile.tilesetId}`,
          "type": "vector"
        });
        const pointAssetConfig = mapFile.fileAssets.find(asset => asset.selected && asset.assetType == AssetType.POINT);
        const lineAssetConfig = mapFile.fileAssets.find(asset => asset.selected && asset.assetType == AssetType.LINE);
        const polygonAssetConfig = mapFile.fileAssets.find(asset => asset.selected && asset.assetType == AssetType.POLYGON);
        const fillAssetConfig = mapFile.fileAssets.find(asset => asset.selected && asset.assetType == AssetType.FILL);
        if (pointAssetConfig) {
          this.map.addLayer(
            {
              'id': layerIdPoint,
              'type': 'circle',
              'source': sourceId,
              'filter': ["==", ["geometry-type"], "Point"],
              'source-layer': 'layer1',
              'layout': {},
              'paint': {
                'circle-color': pointAssetConfig.color,
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  0, 0.5,
                  3, 1,
                  8, 5,
                  15, 5
                ]
              }
            }
          );
          this.map.on('click', layerIdPoint, this.onClickTileset);
        }

        if (lineAssetConfig) {
          this.map.addLayer(
            {
              'id': layerIdLine,
              'type': 'line',
              'source': sourceId,
              'filter': ["==", ["geometry-type"], "LineString"],
              'source-layer': 'layer1',
              'layout': {
                'line-join': 'round',
                'line-cap': 'round'
              },
              'paint': {
                'line-color': lineAssetConfig.color,
                'line-width': 1
              }
            }
          );
          this.map.on('click', layerIdLine, this.onClickTileset);
        }

        if (fillAssetConfig) {
          this.map.addLayer(
            {
              'id': layerIdFill,
              'type': 'fill',
              'source': sourceId,
              'filter': ["==", ["geometry-type"], "Polygon"],
              'source-layer': 'layer1',
              'layout': {},
              'paint': {
                'fill-color': fillAssetConfig.color,
                'fill-opacity': 0.2
              }
            }
          );
          this.map.on('click', layerIdFill, this.onClickTileset);
        }

        if (polygonAssetConfig) {
          this.map.addLayer(
            {
              'id': layerIdFillLine,
              'type': 'line',
              'source': sourceId,
              'filter': ["==", ["geometry-type"], "Polygon"],
              'source-layer': 'layer1',
              'layout': {
                'line-join': 'round',
                'line-cap': 'round'
              },
              'paint': {
                'line-color': polygonAssetConfig.color,
                'line-width': 1
              }
            }
          );
          this.map.on('click', layerIdFillLine, this.onClickTileset);
        }
      }
    });
  }

  onClickTileset = (e) => {
    if (!this.displayTilesetMetadata && (!this.tilesetId || this.tilesetId != e.features[0].layer.id)) {
      this.tilesetId = e.features[0].layer.source;
      this.store.dispatch(mapFilesActions.getFileMetadata({
        tilesetId: this.tilesetId,
        lon: e.lngLat.lng,
        lat: e.lngLat.lat
      }));
    }
  }

  addDrawingToolData(featureCollection) {
    this.polygonControl.set(featureCollection);
  }

  changeStyle(layerId: string, initial = false) {
    if (!initial) {
      this.store.dispatch(polygonActions.bkPolygonData({polygonsFC: this.polygonControl.getAll()}));
    }
    this.style = `mapbox://styles/mapbox/${layerId}-v9`;
    this.store.dispatch(mapConfigActions.mapStyleChanged({layerId}));

    this.updateLayerVisibility();


    Promise.resolve().then(() => {
      setTimeout(() => {
        this.store.dispatch(mapFilesActions.reloadFiles());
      }, 1000);
    });
  }

  private updateLayerVisibility(): void {
    if (!this.paintAreas || this.layerId === 'satellite') {
      const layersToHide = [MARKET_LABEL_LAYER_ID, MARKET_CIRCLE_FILL_LAYER_ID, MARKET_CIRCLE_LAYER_ID];
      layersToHide.forEach(layerId => {
        setTimeout(() => {
          if (this.map && this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, 'visibility', 'none');
          }
        }, 100);
      });
    } else {
      const layersToShow = [MARKET_LABEL_LAYER_ID, MARKET_CIRCLE_FILL_LAYER_ID, MARKET_CIRCLE_LAYER_ID];
      layersToShow.forEach(layerId => {
        setTimeout(() => {
          if (this.map && this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, 'visibility', 'visible');
          }
        }, 100);
      });
    }
  }

  toggleCollapse() {
    if (!this.legendCollapsed) {
      this.lastKnownWidth = this.resizableContainer.nativeElement.style.width;
    }
    this.legendCollapsed = !this.legendCollapsed;
    if (!this.legendCollapsed && this.lastKnownWidth) {
      setTimeout(() => {
        this.renderer.setStyle(this.resizableContainer.nativeElement, 'width', this.lastKnownWidth);
      });
    }
  }

  toggleMapLines() {
    this.displayLines = !this.displayLines;
    this.polygonsHandler.toggleLinesVisibility(this.map, this.displayLines);
  }

  toggleMarkers() {
    this.displayMarkers = !this.displayMarkers;
  }

  toggleMapPolygons() {
    this.displayPolygons = !this.displayPolygons;
    this.polygonsHandler.togglePolygonsVisibility(this.map, this.displayPolygons);
  }

  saveMapData() {
    this.store.dispatch(mapConfigActions.saveMap({
      polygons: this.polygonControl.getAll(),
      labels: this.labelsHandler.getLabelsFromSource(this.map)
    }));
  }

  openPopup(data: DatacenterV2): DynamicDialogRef {
    return this.dialogService.open(NewDataCenterPopupComponent, {
      header: 'Data Center',
      width: '40%',
      data: {
        ...data,
        mapCreationDate: this.mapCreationDate,
      },
      contentStyle: {overflow: 'auto'},
      baseZIndex: 10000,
      maximizable: false,
    });
  }

  addDataCenter() {
    const center = this.map.getCenter()
    const initDatacenter: DatacenterV2 = {
      id: null,
      auxId: this.mapService.buildAuxId(),
      lat: center.lat,
      lon: center.lng,
    } as DatacenterV2;
    this.openPopup(initDatacenter).onClose.subscribe(
      (newMarkerData: DatacenterV2) => {
        if (newMarkerData) {
          this.store.dispatch(
            datacenterActions.datacenterModified({datacenter: newMarkerData})
          );
        }
      }
    );
  }

  saveLabel(labelText: any) {
    const center = this.map.getCenter();
    this.labelsHandler.addLabelToMap(this.map, labelText.value, center.lat, center.lng);
    this.labelModalVisible = false;
    labelText.value = '';
  }


  centerMap(info: GeoInfo) {
    if (info.zoom) {
      this.map.flyTo({
        center: [info.longitude, info.latitude],
        zoom: info.zoom,
        essential: true,
      });
    } else {
      this.map.flyTo({
        center: [info.longitude, info.latitude],
        essential: true,
      });
    }
  }

  onResizing(event: ResizeEvent): void {
    this.isResizing = true;
    if (event.rectangle.width && Math.abs(event.rectangle.width - this.resizeStartX) > 5) {
      this.ngZone.runOutsideAngular(() => {
        this.renderer.setStyle(this.resizableContainer.nativeElement, 'width', `${event.rectangle.width}px`);
      });
    }
  }

  onResizeEnd(event: ResizeEvent): void {
    if (event.rectangle.width && Math.abs(event.rectangle.width - this.resizeStartX) > 5) {
      this.lastKnownWidth = `${event.rectangle.width}px`;
      this.renderer.setStyle(this.resizableContainer.nativeElement, 'width', this.lastKnownWidth);
    }
    setTimeout(() => {
      this.isResizing = false;
    }, 0);
  }

  onResizeStart(event: ResizeEvent): void {
    this.isResizing = true;
    this.resizeStartX = event.rectangle.width;
  }

  toggleMarkersDrag() {
    this.store.dispatch(mapConfigActions.toggleMarkersDrag());
  }

  unitsChanged(event) {
    this.store.dispatch(
      mapConfigActions.distanceUnitsChanged({
        units: event.checked ? DistanceUnits.MILES : DistanceUnits.KMS,
      })
    );
  }

  async triggerCsvDownload() {
    const {_id, mapName} = await firstValueFrom(this.userMap);
    this.fileDownloader.downloadCsv(_id, mapName);
  }

  toggleFullscreen = (): void => {
    const body = document.body;
    const mainToolbar = document.getElementById('main-toolbar');

    this.isFullscreen = !this.isFullscreen;

    if (mainToolbar) {
      mainToolbar.style.display = this.isFullscreen ? 'none' : 'block';
    }

    if (this.isFullscreen) {
      this.enterFullscreen(body);
    } else {
      this.exitFullscreen();
    }

    this.resizeMapAfterDelay();
  }

  private enterFullscreen(element: HTMLElement): void {
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      (element as any).mozRequestFullScreen();
    } else if ((element as any).webkitRequestFullscreen) {
      (element as any).webkitRequestFullscreen();
    } else if ((element as any).msRequestFullscreen) {
      (element as any).msRequestFullscreen();
    }
  }

  private exitFullscreen(): void {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
  }

  private resizeMapAfterDelay(): void {
    setTimeout(() => {
      if (this.map) {
        this.map.resize();
      }
      this.cdr.detectChanges();
    }, 200);
  }

  handleFullscreenChange = () => {
    this.isFullscreen = !!document.fullscreenElement;
    setTimeout(() => {
      if (this.map) {
        this.map.resize();
      }
      this.cdr.detectChanges();
    }, 200);
  }

  showLayerPopup(layer: MapLayer) {
    if (this.layerPopup) {
      this.layerPopup.remove();
    }

    const popupContent = this.dynamicComponentService.injectComponent(
      MapLayerPopupComponent,
      (component) => {
        component.layer = layer;
      }
    );

    this.layerPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: false,
      className: 'map-layer-popup'
    })
      .setLngLat(this.map.getCenter())
      .setDOMContent(popupContent)
      .addTo(this.map);
  }

  onMetadataClose() {
    this.displayTilesetMetadata = false;
    this.tilesetId = "";
  }

  get metadataFields(): String[] {

    const labels: String[] = [];

    return labels;
  }

  getMapTypeFriendlyName(mapType: string): string {
    switch (mapType) {
      case 'fiber':
        return 'Fiber';
      case 'power':
        return 'Power';
      case 'land':
        return 'Land';
      case 'railroad':
        return 'Railroad';
      case 'oilGas':
        return 'Oil / Gas';
      default:
        return "";
    }
  }
}

class CustomFullscreenControl implements mapboxgl.IControl {
  private container: HTMLElement;
  private fullscreenButton: HTMLButtonElement;
  private map: mapboxgl.Map;
  private toggleFullscreen: () => void;
  private isFullscreen: boolean = false;

  constructor(toggleFullscreen: () => void) {
    this.toggleFullscreen = toggleFullscreen;
  }

  onAdd(map: mapboxgl.Map) {
    this.map = map;
    this.container = document.createElement('div');
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    this.fullscreenButton = document.createElement('button');
    this.fullscreenButton.className = 'mapboxgl-ctrl-fullscreen';
    this.fullscreenButton.type = 'button';
    this.fullscreenButton.setAttribute('aria-label', 'Toggle fullscreen');
    this.fullscreenButton.onclick = () => {
      this.isFullscreen = !this.isFullscreen;
      this.updateButtonState();
      this.toggleFullscreen();
    };
    this.container.appendChild(this.fullscreenButton);
    return this.container;
  }

  onRemove() {
    this.container.parentNode.removeChild(this.container);
    this.map = undefined;
  }

  private updateButtonState() {
    if (this.isFullscreen) {
      this.fullscreenButton.className = 'mapboxgl-ctrl-shrink';
      this.fullscreenButton.setAttribute('aria-label', 'Exit fullscreen');
    } else {
      this.fullscreenButton.className = 'mapboxgl-ctrl-fullscreen';
      this.fullscreenButton.setAttribute('aria-label', 'Enter fullscreen');
    }
  }
}
